/**
 * TARİH ARALIĞI ÇÖZÜMLEYİCİ — saf, I/O yok.
 *
 * NİÇİN AYRI DOSYA: rapor aralıkları sessizce yanlış rakam üreten türden bir
 * iştir. Eski `startOfMonth` şunu yapıyordu:
 *
 *     new Date(d.getFullYear(), d.getMonth(), 1)
 *
 * Bu, çalışma zamanının YEREL saatini kullanır — Cloud Functions'ta UTC.
 * `timeZone: "Europe/Istanbul"` yalnızca cron'un NE ZAMAN tetikleneceğini
 * etkiler, hesabı değil. Sonuç: ay sınırı 1'i 00:00 UTC = 1'i 03:00 İstanbul.
 * 1 Eylül 01:30'da (TR) verilen bir sipariş "ağustos" cirosuna yazılıyordu ve
 * ay kapanışında iki ay birden yanlış çıkıyordu. Hiçbir hata mesajı yok.
 *
 * Buradaki her şey saf olduğu için emülatörsüz, saniyeler içinde test edilir.
 *
 * ── "SON N GÜN" TANIMI ──────────────────────────────────────────────────────
 * Shopify ile BİREBİR aynı: "Son 7 gün" = DÜNLE BİTEN 7 gün, bugün HARİÇ.
 * Sebebi tercih değil karşılaştırılabilirlik: kullanıcı aynı rakamı Shopify
 * panelinde de görüyor; iki sistem farklı tanım kullanırsa sayılar tutmaz ve
 * hangisinin doğru olduğu tartışılır. Bugünü görmek için "Bugün" ön ayarı var.
 * Arayüz çözümlenen tarihleri her zaman yazar, böylece tanım görünür kalır.
 */

export const REPORT_TZ = "Europe/Istanbul";

export type RangePreset =
  | "today"
  | "yesterday"
  | "today_yesterday"
  | "last_7"
  | "last_14"
  | "last_28"
  | "last_30"
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "max"
  | "custom";

export const RANGE_LABELS: Record<RangePreset, string> = {
  today: "Bugün",
  yesterday: "Dün",
  today_yesterday: "Bugün ve dün",
  last_7: "Son 7 gün",
  last_14: "Son 14 gün",
  last_28: "Son 28 gün",
  last_30: "Son 30 gün",
  this_week: "Bu hafta",
  last_week: "Geçen hafta",
  this_month: "Bu ay",
  last_month: "Geçen ay",
  max: "Maksimum",
  custom: "Özel",
};

/** Ekranda görünen sıra — Shopify'daki listeyle aynı. */
export const RANGE_ORDER: RangePreset[] = [
  "today", "yesterday", "today_yesterday",
  "last_7", "last_14", "last_28", "last_30",
  "this_week", "last_week", "this_month", "last_month",
  "max", "custom",
];

export interface ResolvedRange {
  preset: RangePreset;
  /** Aralığın başı — DAHİL. */
  start: Date;
  /** Aralığın sonu — HARİÇ (yarı açık aralık; sınır çakışması olmaz). */
  end: Date;
  /** Önceki eşit uzunlukta dönem — karşılaştırma için. `max`'ta yoktur. */
  prevStart: Date | null;
  prevEnd: Date | null;
  /** "12.08.2026 – 18.08.2026" — tanımın görünür kalması için. */
  label: string;
  /** Kaç takvim günü kapsıyor (etiket ve önbellek anahtarı için). */
  days: number;
}

/* ── Saat dilimi yardımcıları ─────────────────────────────────────────────── */

/** Verilen anın hedef saat dilimindeki takvim parçaları. */
function partsIn(at: Date, tz: string) {
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(at)) if (x.type !== "literal") p[x.type] = x.value;
  // `hour12: false` bazı ortamlarda gece yarısını "24" verir.
  const hour = p.hour === "24" ? 0 : Number(p.hour);
  return {
    y: Number(p.year), m: Number(p.month), d: Number(p.day),
    h: hour, mi: Number(p.minute), s: Number(p.second),
  };
}

/** Hedef saat diliminin o andaki UTC ofseti (dakika). */
function offsetMinutes(at: Date, tz: string): number {
  const p = partsIn(at, tz);
  const asIfUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
  return Math.round((asIfUtc - at.getTime()) / 60000);
}

/**
 * Hedef saat dilimindeki bir yerel zamanı gerçek UTC anına çevirir.
 *
 * İki tur döner: ofset, sonucun kendisine bağlıdır (yaz saati sınırında ilk
 * tahmin yanlış ofseti kullanabilir). Türkiye 2016'dan beri sabit UTC+3, ama
 * kural değişirse bu döngü sessizce yanlış cevap vermek yerine doğruyu bulur.
 */
export function zonedToUtc(
  y: number, m: number, d: number,
  h = 0, mi = 0, s = 0,
  tz: string = REPORT_TZ
): Date {
  const naive = Date.UTC(y, m - 1, d, h, mi, s);
  let out = new Date(naive);
  for (let i = 0; i < 2; i++) out = new Date(naive - offsetMinutes(out, tz) * 60000);
  return out;
}

/** Verilen anın, hedef saat dilimindeki gün başlangıcı (00:00). */
export function startOfDay(at: Date, tz: string = REPORT_TZ): Date {
  const p = partsIn(at, tz);
  return zonedToUtc(p.y, p.m, p.d, 0, 0, 0, tz);
}

/** Gün ekler — takvim günü olarak (saat aritmetiği değil). */
export function addDays(at: Date, n: number, tz: string = REPORT_TZ): Date {
  const p = partsIn(at, tz);
  return zonedToUtc(p.y, p.m, p.d + n, p.h, p.mi, p.s, tz);
}

/** Haftanın başı — PAZARTESİ (TR kullanımı; `getUTCDay` pazarı 0 sayar). */
export function startOfWeek(at: Date, tz: string = REPORT_TZ): Date {
  const sod = startOfDay(at, tz);
  const p = partsIn(sod, tz);
  // Yerel takvim gününün haftanın kaçıncı günü olduğu:
  const dow = new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay(); // 0=Paz
  const back = dow === 0 ? 6 : dow - 1;
  return zonedToUtc(p.y, p.m, p.d - back, 0, 0, 0, tz);
}

export function startOfMonth(at: Date, tz: string = REPORT_TZ): Date {
  const p = partsIn(at, tz);
  return zonedToUtc(p.y, p.m, 1, 0, 0, 0, tz);
}

/** "gg.aa.yyyy" — hedef saat diliminde. */
export function trDate(at: Date, tz: string = REPORT_TZ): string {
  const p = partsIn(at, tz);
  const z = (n: number) => String(n).padStart(2, "0");
  return `${z(p.d)}.${z(p.m)}.${p.y}`;
}

/** Sistemin ilk verisinden önceki bir tarih — "Maksimum" için alt sınır. */
const EPOCH = new Date(Date.UTC(2015, 0, 1));

/* ── Çözümleyici ──────────────────────────────────────────────────────────── */

export interface RangeInput {
  preset: RangePreset;
  /** `custom` için "yyyy-aa-gg" (dahil). */
  from?: string | null;
  /** `custom` için "yyyy-aa-gg" (DAHİL — gün sonuna kadar sayılır). */
  to?: string | null;
}

/** "yyyy-aa-gg" → o günün hedef saat dilimindeki başlangıcı. */
function parseDay(iso: string, tz: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  const out = zonedToUtc(Number(y), Number(mo), Number(d), 0, 0, 0, tz);
  return Number.isNaN(out.getTime()) ? null : out;
}

export function resolveRange(
  input: RangeInput,
  now: Date = new Date(),
  tz: string = REPORT_TZ
): ResolvedRange {
  const today = startOfDay(now, tz);
  const tomorrow = addDays(today, 1, tz);

  let start: Date;
  let end: Date;

  switch (input.preset) {
    case "today":
      start = today; end = tomorrow; break;

    case "yesterday":
      start = addDays(today, -1, tz); end = today; break;

    case "today_yesterday":
      start = addDays(today, -1, tz); end = tomorrow; break;

    // ★ Shopify tanımı: bugün HARİÇ, dünle biten N gün.
    case "last_7":
      start = addDays(today, -7, tz); end = today; break;
    case "last_14":
      start = addDays(today, -14, tz); end = today; break;
    case "last_28":
      start = addDays(today, -28, tz); end = today; break;
    case "last_30":
      start = addDays(today, -30, tz); end = today; break;

    case "this_week":
      start = startOfWeek(now, tz); end = tomorrow; break;

    case "last_week": {
      const thisWeek = startOfWeek(now, tz);
      start = addDays(thisWeek, -7, tz); end = thisWeek; break;
    }

    case "this_month":
      start = startOfMonth(now, tz); end = tomorrow; break;

    case "last_month": {
      const thisMonth = startOfMonth(now, tz);
      start = startOfMonth(addDays(thisMonth, -1, tz), tz);
      end = thisMonth;
      break;
    }

    case "max":
      start = EPOCH; end = tomorrow; break;

    case "custom": {
      const f = input.from ? parseDay(input.from, tz) : null;
      const t = input.to ? parseDay(input.to, tz) : null;
      if (!f || !t) {
        throw new Error("Özel aralık için başlangıç ve bitiş tarihi gerekli (yyyy-aa-gg).");
      }
      // Bitiş tarihi DAHİL: kullanıcı "1–31 Ağustos" derse 31'i de sayılmalı.
      start = f <= t ? f : t;
      end = addDays(f <= t ? t : f, 1, tz);
      break;
    }
  }

  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000));
  const span = end.getTime() - start.getTime();

  // Önceki eşit dönem — "Maksimum"da anlamsız (öncesi yok).
  const hasPrev = input.preset !== "max";
  const prevEnd = hasPrev ? new Date(start.getTime()) : null;
  const prevStart = hasPrev ? new Date(start.getTime() - span) : null;

  const lastDay = addDays(end, -1, tz);
  const label =
    trDate(start, tz) === trDate(lastDay, tz)
      ? trDate(start, tz)
      : `${trDate(start, tz)} – ${trDate(lastDay, tz)}`;

  return { preset: input.preset, start, end, prevStart, prevEnd, label, days };
}
